import { useCallback, useRef, useState } from 'react';
import {
	type OperationVariables,
	type MutationResult,
	type MutationFunctionOptions,
} from '@apollo/react-common';
import {
	type MutationHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useMutation,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, commitMutation, useRelayEnvironment } from 'react-relay';

import { isRelayEnabled } from './isRelayEnabled.ts';

import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type MutationFunctionInteropSupportedOptions<TData, TVariables> = Pick<
	MutationFunctionOptions<TData, TVariables>,
	'variables' | 'optimisticResponse' | 'update'
> & {
	// @deprecated - use mutation options instead
	refetchQueries?: never;
};

export type MutationFunctionInterop<TData, TVariables> = (
	options?: MutationFunctionInteropSupportedOptions<TData, TVariables>,
) => Promise<{ data?: TData }>;

export type UseMutationInteropSupportedResult<TData, TVariables> = [
	MutationFunctionInterop<TData, TVariables>,
	Pick<MutationResult<TData>, 'data' | 'error' | 'loading' | 'called'>,
];

/** Helper type to allow for easier overrides ion the `UseMutationInteropSupportedArgs` type */
type MutationUpdateParamType<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Required<MutationHookOptions<TData, TVariables>>['update'];

export type UseMutationInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [
			GraphQLTaggedNode,
			Pick<
				MutationHookOptions<TData, TVariables>,
				| 'variables'
				| 'onCompleted'
				| 'onError'
				| 'errorPolicy'
				| 'optimisticResponse'
				| 'context'
				| 'fetchPolicy'
				| 'awaitRefetchQueries'
			> & {
				refetchQueries?:
					| Array<{ query: GraphQLTaggedNode; variables?: any }>
					| ((response: { data: TData }) => Array<{ query: GraphQLTaggedNode; variables?: any }>);
				update?(
					_: never,
					mutationResult: Parameters<MutationUpdateParamType<TData, TVariables>>[1],
				): ReturnType<MutationUpdateParamType<TData, TVariables>>;
				/**
				 * Ensures that the interop in Apollo build variant uses the AGG client
				 */
				interopApolloAggClient?: boolean;
			},
	  ];

export function useMutationInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...args: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	if (isRelayEnabled()) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutationInteropRelay<TData, TVariables>(...args);
	} else {
		const { interopApolloAggClient, ...apolloOptions } = args[1] ?? {};
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutation<TData, TVariables>(
			...[
				toDocumentNode(args[0]),
				{
					...apolloOptions,
					...(interopApolloAggClient ? { client: window['GLOBAL_APOLLO_AGG_CLIENT'] } : {}),
					refetchQueries: createRefetchQueriesFn(),
					update(_: any, result: Parameters<MutationUpdateParamType<TData, TVariables>>[1]) {
						return apolloOptions?.update?.(null as never, result);
					},
				},
			],
		);
	}

	function createRefetchQueriesFn() {
		if (typeof args[1]?.refetchQueries === 'undefined') return undefined;

		return (response: { data: TData }) => {
			const graphqlTaggedNodeArray =
				typeof args[1]?.refetchQueries === 'function'
					? args[1]?.refetchQueries(response)
					: (args[1]?.refetchQueries ?? []);

			return graphqlTaggedNodeArray?.map((value) => {
				if (typeof value === 'string') {
					// handling the case where simply the query name is specified, e.g. `refetchQueries: ['SomeQuery']`
					return value;
				}

				return {
					query: toDocumentNode(value.query),
					variables: value.variables,
				};
			});
		};
	}
}

function useMutationInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...[mutation, options]: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	// TODO: shim accepted but not yet supported parameters (i.e. those params WILL be supported later)
	const unsupportedParams = [
		'awaitRefetchQueries',
		'optimisticResponse',
		'update',
		'refetchQueries',
	];
	const unsupportedParam = unsupportedParams.find((param) => options && param in options);
	if (unsupportedParam) {
		throw new Error(`[Relay migration] ${unsupportedParam} not supported yet (but should be)`);
	}

	const environment = useRelayEnvironment();
	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables);

	// Extract context and memoize
	const context = useMemoObj(options?.context);

	// State to manage the mutation result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<any>(undefined);
	const [called, setCalled] = useState(false);

	const onCompletedRef = useRef(options?.onCompleted);
	onCompletedRef.current = options?.onCompleted;

	// Create the mutation function
	const mutate = useCallback(
		(mutationOptions?: MutationFunctionInteropSupportedOptions<TData, TVariables>) => {
			const { optimisticResponse } = mutationOptions || {};
			setCalled(true);
			setLoading(true);
			setError(undefined);

			// TODO: shim accepted but unsupported parameters
			if (optimisticResponse) {
				throw new Error('[Relay migration] optimisticResponse not supported yet');
			}

			const operationVariables = mutationOptions?.variables || variables || {};

			return new Promise<{ data?: TData }>((resolve, reject) => {
				commitMutation(environment, {
					mutation,
					cacheConfig: { metadata: context },
					variables: operationVariables,
					onCompleted: (response: any, errors) => {
						if (errors && errors.length > 0) {
							const error = new Error(errors[0].message);
							setError(error);
							reject(error);
						} else {
							setData(response as TData);
							// Call the user-provided onCompleted callback only on success (to match Apollo behavior)
							onCompletedRef.current?.(response);
							resolve({ data: response as TData });
						}
						setLoading(false);
					},
					onError: (error: any) => {
						setLoading(false);
						setError(error);
						reject(error);
					},
				});
			});
		},
		[environment, mutation, variables, context],
	);

	return [
		mutate,
		{
			data,
			loading,
			error,
			called,
		},
	];
}
